import { Link } from 'react-router-dom'
import { Star } from 'lucide-react'
import { Tool } from '../../types'
import { isFreePricing, toolIdToSlug } from '../../lib/utils'
import { cn } from '../../lib/utils'

interface ToolCardProps {
  tool: Tool
  rank?: number
  /** list: App Store SmallLockup 行；tile: 方卡 */
  variant?: 'list' | 'tile'
  /** list 默认不显示评分，更接近 App Store shelf */
  showRating?: boolean
  /** 排行列表使用更突出的层级与悬停反馈 */
  chartStyle?: boolean
  className?: string
}

const ctaClassName =
  'rounded-full bg-secondary text-primary font-semibold flex items-center justify-center hover:bg-secondary/70 dark:hover:bg-secondary/80 transition-colors'

export function ToolCard({
  tool,
  rank,
  variant = 'list',
  showRating = false,
  chartStyle = false,
  className,
}: ToolCardProps) {
  const isFree = isFreePricing(tool.pricing)
  const detailHref = `/item/${toolIdToSlug(tool.id)}`
  const cta = isFree ? '获取' : '查看'

  // 「获取」直接跳转外链；图标/标题仍进站内详情
  const externalCta = (
    <a
      href={tool.url}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        ctaClassName,
        variant === 'tile'
          ? 'mt-2.5 self-start h-7 min-w-[3.25rem] px-3.5 text-[12px]'
          : 'shrink-0 h-7 min-w-[3.25rem] px-3.5 text-[13px]',
      )}
    >
      {cta}
    </a>
  )

  if (chartStyle) {
    return (
      <article
        className={cn(
          'relative flex h-[304px] w-[260px] shrink-0 snap-start flex-col items-center rounded-[20px] bg-secondary/70 px-5 pb-6 pt-7 transition-colors hover:bg-secondary sm:w-[calc((100%_-_1.25rem)/2)] lg:w-[calc((100%_-_3rem)/3)] xl:w-[calc((100%_-_4.5rem)/4)] 2xl:w-[calc((100%_-_6rem)/5)]',
          className,
        )}
      >
        {rank !== undefined ? (
          <span className="absolute left-4 top-4 text-[17px] font-normal leading-none text-muted-foreground tabular-nums">
            {rank}
          </span>
        ) : null}

        <Link to={detailHref} className="shrink-0">
          <img
            src={tool.image}
            alt={tool.name}
            className="h-[100px] w-[100px] rounded-[22px] bg-white object-cover ring-1 ring-black/[0.04] dark:bg-white/5 dark:ring-white/10"
            loading="lazy"
            decoding="async"
          />
        </Link>

        <Link to={detailHref} className="mt-3.5 w-full min-w-0 text-center">
          <h3 className="truncate text-[17px] font-medium leading-[22px] text-foreground">
            {tool.name}
          </h3>
          <p className="mx-auto mt-1.5 min-h-10 max-w-[240px] text-[13px] leading-[19px] text-muted-foreground line-clamp-2">
            {tool.description}
          </p>
        </Link>

        <a
          href={tool.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-auto inline-flex h-8 min-w-16 items-center justify-center rounded-full bg-background/70 px-4 text-[13px] font-semibold text-primary transition-colors hover:bg-background"
        >
          {cta}
        </a>
      </article>
    )
  }

  if (variant === 'tile') {
    return (
      <div
        className={cn(
          'group flex flex-col w-[136px] sm:w-[148px] shrink-0',
          className,
        )}
      >
        <Link to={detailHref} className="block mb-2.5">
          <img
            src={tool.image}
            alt={tool.name}
            loading="lazy"
            decoding="async"
            className="w-full aspect-square rounded-[22.5%] object-cover bg-white border border-black/[0.04] dark:border-white/10 dark:bg-white/5"
          />
        </Link>
        <Link to={detailHref} className="min-w-0 flex-1">
          <h3 className="font-normal text-[13px] leading-snug line-clamp-2 text-foreground">
            {tool.name}
          </h3>
          <p className="text-[12px] text-muted-foreground line-clamp-1 mt-0.5">
            {tool.description}
          </p>
        </Link>
        {externalCta}
      </div>
    )
  }

  // App Store SmallLockup: 64px icon · title · 1-line subtitle · gray Get
  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 py-2.5 min-h-[72px] transition-colors',
        className,
      )}
    >
      {rank !== undefined && (
        <span
          className={cn(
            'text-[17px] font-semibold w-5 shrink-0 text-center tabular-nums leading-none',
            rank <= 3 ? 'text-primary' : 'text-muted-foreground/40',
          )}
        >
          {rank}
        </span>
      )}

      <Link to={detailHref} className="shrink-0">
        <img
          src={tool.image}
          alt={tool.name}
          className="w-16 h-16 rounded-[22.5%] object-cover bg-white border border-black/[0.04] dark:border-white/10 dark:bg-white/5"
          loading="lazy"
          decoding="async"
        />
      </Link>

      <Link to={detailHref} className="flex-1 min-w-0 self-center">
        <h3 className="font-normal text-[15px] leading-snug line-clamp-1">{tool.name}</h3>
        <p className="text-[13px] text-muted-foreground line-clamp-1 mt-0.5">
          {tool.description}
        </p>
        {showRating ? (
          <div className="flex items-center gap-1 mt-0.5 text-muted-foreground">
            <Star className="w-3 h-3 fill-current" />
            <span className="text-[11px] font-medium tabular-nums">{tool.rating}</span>
          </div>
        ) : null}
      </Link>

      {externalCta}
    </div>
  )
}
